import { t } from '@lingui/macro'
import _ from 'lodash'
import { useSnackbar } from 'notistack'
import { useMemo, useRef } from 'react'

import {
  useCreateSpecialityOptionMutation,
  useDeleteSpecialityOptionMutation,
  useGetSpecialityOptionsQuery,
  useUpdateSpecialityOptionMutation,
} from '@/core/services/speciality-option'

import { SpecialityOptionData, SpecialityOptionsHandle } from '../ui/SpecialityOptions'

export function useSpecialityOptions() {
  const { enqueueSnackbar } = useSnackbar()
  const specialityOptionsRef = useRef<SpecialityOptionsHandle>(null)

  // Fetch speciality options
  const { data: specialityOptions = [], isLoading: isListing, refetch } = useGetSpecialityOptionsQuery({})

  // Transform data for UI
  const specialityOptionsData = useMemo(
    () =>
      _.map(specialityOptions, (specialityOption) => ({
        id: specialityOption._id,
        code: specialityOption.code,
        name: specialityOption.name,
        selectByDefault: specialityOption.selectByDefault || false,
      })),
    [specialityOptions]
  )

  // Mutations
  const [createSpecialityOption, { isLoading: isCreating }] = useCreateSpecialityOptionMutation()
  const [updateSpecialityOption, { isLoading: isUpdating }] = useUpdateSpecialityOptionMutation()
  const [deleteSpecialityOption, { isLoading: isDeleting }] = useDeleteSpecialityOptionMutation()

  // Handlers
  const handleCreate = async (values: Partial<SpecialityOptionData>) => {
    if (values.code && values.name) {
      try {
        await createSpecialityOption({
          code: values.code,
          name: values.name,
          selectByDefault: values.selectByDefault || false,
        }).unwrap()
        enqueueSnackbar(t`Speciality option created successfully`, { variant: 'success' })
        specialityOptionsRef.current?.toggleOptionFormDialog()
        // Refetch if selectByDefault was set to true
        if (values.selectByDefault) {
          refetch()
        }
      } catch (error) {
        enqueueSnackbar(t`Unable to create speciality option`, { variant: 'error' })
      }
    }
  }

  const handleUpdate = async (specialityOptionId: string, values: Partial<SpecialityOptionData>) => {
    try {
      const specialityOptionData = _.find(specialityOptionsData, ['id', specialityOptionId])
      const changes = _.pickBy(values, (value, key) => _.get(specialityOptionData, key) !== value)

      await updateSpecialityOption({
        id: specialityOptionId,
        changes,
      }).unwrap()
      enqueueSnackbar(t`Speciality option updated successfully`, { variant: 'success' })
      specialityOptionsRef.current?.toggleOptionFormDialog()
      // Refetch if selectByDefault was updated to true
      if (changes.selectByDefault) {
        refetch()
      }
    } catch (error) {
      enqueueSnackbar(t`Unable to update speciality option`, { variant: 'error' })
    }
  }

  const handleDelete = async (specialityOptionId: string) => {
    try {
      await deleteSpecialityOption(specialityOptionId).unwrap()
      enqueueSnackbar(t`Speciality option deleted successfully`, { variant: 'success' })
    } catch (error) {
      enqueueSnackbar(t`Unable to delete speciality option`, { variant: 'error' })
    }
  }

  const handleSearch = (text: string) => {
    refetch()
  }

  return {
    data: specialityOptionsData,
    isCreating,
    isDeleting,
    isListing,
    isUpdating,
    onCreate: handleCreate,
    onDelete: handleDelete,
    onSearch: handleSearch,
    onUpdate: handleUpdate,
    ref: specialityOptionsRef,
  }
}
